import type { PlaybackDirection } from '../project/ProjectDocument.js'
import type { EditorViewport, PlaybackFrame } from '../store/commands.js'
import type { V2TransientState } from './V2Runtime.js'

export interface LegacyV2State {
  readonly viewport: EditorViewport
  readonly orderAmount: number
  readonly isPlaying: boolean
  readonly playbackDirection: PlaybackDirection
  readonly renderingEnabled: boolean
  readonly selectedWordIndex: number | null
  readonly selectedLetterIndex: number | null
  readonly selectedDotIndex: number | null
}

function transientShell(state: LegacyV2State, playback: PlaybackFrame): V2TransientState {
  return {
    viewport: state.viewport,
    playback,
    renderingEnabled: state.renderingEnabled,
    selection: {
      wordIndex: state.selectedWordIndex,
      letterIndex: state.selectedLetterIndex,
      dotIndex: state.selectedDotIndex,
    },
  }
}

/** Seed the runtime exactly once when V1 hands ownership to V2. */
export function initialTransientFromLegacy(state: LegacyV2State): V2TransientState {
  const direction = state.playbackDirection === 'reverse'
    ? -1
    : state.playbackDirection === 'pingPong' && state.orderAmount > 0.5
      ? -1
      : 1
  return transientShell(state, {
    orderAmount: state.orderAmount,
    isPlaying: state.isPlaying,
    direction,
    phase: state.isPlaying ? 'moving' : 'complete',
  })
}

/**
 * Subsequent React updates must not replace scheduler-owned progress, phase, or
 * direction. Only low-frequency play intent is mirrored from legacy state.
 */
export function transientFromLegacyPreservingPlayback(
  state: LegacyV2State,
  playback: PlaybackFrame,
): V2TransientState {
  return transientShell(state, playback.isPlaying === state.isPlaying
    ? playback
    : { ...playback, isPlaying: state.isPlaying })
}
